import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
} from 'firebase/firestore';

import { auth, db } from '../firebase';
import { authedFetch } from '../lib/authedFetch';
import type { SiteEntity } from '../types/site';
import {
  type PathToGoDecisionEvidenceSnapshot,
  type PathToGoDecisionRecord,
  type PathToGoHumanDecision,
  type PathToGoDecisionStage,
  pathToGoDecisionStage,
} from '../types/pathToGoDecision';
import { screeningPositiveDecisionAllowed } from '../lib/pathToGo/screeningDecisionSnapshot';

const RECORDS_COLLECTION = 'decisionRecords';

function ownerUidForSite(site: Pick<SiteEntity, '_ownerUid'>): string {
  const uid = site._ownerUid ?? auth.currentUser?.uid;
  if (!uid) throw new Error('Sign in to record a site decision.');
  return uid;
}

async function decisionApiRecord(
  path: '/submit' | '/review',
  body: Record<string, unknown>,
): Promise<PathToGoDecisionRecord> {
  const response = await authedFetch(`/api/path-to-go/decisions${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({})) as {
    record?: PathToGoDecisionRecord;
    error?: string;
  };
  if (!response.ok || !payload.record) {
    throw new Error(payload.error || `Decision request failed (${response.status}).`);
  }
  return payload.record;
}

export interface SubmitPathToGoDecisionInput {
  decisionStage?: PathToGoDecisionStage;
  /** Reuse the same UUID when retrying this Stage 1 submission. */
  submissionId?: string;
  site: Pick<SiteEntity, 'id' | '_ownerUid'>;
  decision: PathToGoHumanDecision;
  rationale: string;
  decisionOwner: string;
  evidence: PathToGoDecisionEvidenceSnapshot;
}

/**
 * Ask the server to rebuild the live decision evidence and atomically create
 * one immutable submitted revision. The caller's evidence is retained only for
 * immediate UX validation; it is never trusted at the persistence boundary.
 */
export async function submitPathToGoDecision(
  input: SubmitPathToGoDecisionInput,
): Promise<PathToGoDecisionRecord> {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in to record a site decision.');
  const rationale = input.rationale.trim();
  const decisionOwner = input.decisionOwner.trim();
  const stage = input.decisionStage ?? 2;
  if (pathToGoDecisionStage(input.evidence) !== stage) {
    throw new Error('Decision evidence belongs to another decision stage.');
  }
  const allowed = stage === 1
    ? ['PROCEED', 'PROCEED_WITH_CONCERNS', 'DECLINE', 'DEFER']
    : ['GO', 'CONDITIONAL', 'NO_GO', 'DEFER'];
  if (!allowed.includes(input.decision)) throw new Error('Decision outcome belongs to another decision stage.');
  if (!rationale) throw new Error('Add a rationale before submitting the decision.');
  if (!decisionOwner) throw new Error('Name the decision owner before submitting the decision.');
  if (stage === 1 && (input.decision === 'PROCEED' || input.decision === 'PROCEED_WITH_CONCERNS')
    && !screeningPositiveDecisionAllowed(input.evidence)) {
    throw new Error('PROCEED and PROCEED WITH CONCERNS require a current eligible saved Screening Assessment.');
  }
  if (
    (input.decision === 'GO' || input.decision === 'CONDITIONAL')
    && !input.evidence.positiveDecisionGateSatisfied
  ) {
    throw new Error(
      'GO and CONDITIONAL cannot be recorded until the decision intent, required analyses, and verification work are current (Intent, Analyses, and Site Evidence tabs).',
    );
  }

  const submissionId = stage === 1 ? input.submissionId ?? crypto.randomUUID() : undefined;
  if (stage === 1 && !/^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(submissionId!)) {
    throw new Error('Invalid screening submission identity.');
  }
  return decisionApiRecord('/submit', {
    siteId: input.site.id,
    ownerUid: ownerUidForSite(input.site),
    decision: input.decision,
    rationale,
    decisionOwner,
    ...(stage === 1 ? { decisionStage: 1, submissionId } : {}),
  });
}

export interface ReviewPathToGoDecisionInput {
  site: Pick<SiteEntity, 'id' | '_ownerUid'>;
  recordId: string;
  outcome: 'approved' | 'rejected';
  rejectionReason?: string;
  /**
   * Required for approving GO/CONDITIONAL. The caller must rebuild this from
   * the live cockpit immediately before review so stale evidence cannot be
   * approved.
   */
  currentEvidence?: PathToGoDecisionEvidenceSnapshot;
}

/**
 * Ask the server to re-resolve current evidence and apply the one allowed
 * terminal review mutation. Approved/rejected records remain immutable.
 */
export async function reviewPathToGoDecision(
  input: ReviewPathToGoDecisionInput,
): Promise<PathToGoDecisionRecord> {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in to review this decision.');
  const ownerUid = ownerUidForSite(input.site);
  const rejectionReason = input.rejectionReason?.trim();
  if (input.outcome === 'rejected' && !rejectionReason) {
    throw new Error('Add a reason before rejecting the decision.');
  }

  return decisionApiRecord('/review', {
    siteId: input.site.id,
    ownerUid,
    recordId: input.recordId,
    outcome: input.outcome,
    ...(rejectionReason ? { rejectionReason } : {}),
  });
}

export async function listPathToGoDecisions(
  site: Pick<SiteEntity, 'id' | '_ownerUid'>,
  maximum = 20,
  decisionStage?: PathToGoDecisionStage,
): Promise<PathToGoDecisionRecord[]> {
  const ownerUid = ownerUidForSite(site);
  if (decisionStage != null) {
    // Follow the immutable same-stage chain from its server-owned pointer.
    // This includes legacy Stage 2 records without requiring new indexes.
    const path = `users/${ownerUid}/sites/${site.id}`;
    const state = await getDoc(doc(db, `${path}/decisionRecordState/${decisionStage === 1 ? 'screening' : 'current'}`));
    let next: unknown = state.exists() ? state.data()?.currentDecisionId : undefined;
    if (state.exists() && (typeof next !== 'string' || !next || next.includes('/'))) {
      throw new Error('Decision history identity is invalid.');
    }
    const rows: PathToGoDecisionRecord[] = [];
    const seen = new Set<string>();
    while (typeof next === 'string' && rows.length < Math.max(1, Math.min(100, maximum))) {
      if (!next || next.includes('/') || seen.has(next)) throw new Error('Decision history identity is invalid.');
      seen.add(next);
      const snapshot = await getDoc(doc(db, `${path}/${RECORDS_COLLECTION}/${next}`));
      if (!snapshot.exists()) throw new Error('Decision history revision is unavailable.');
      const record = snapshot.data() as PathToGoDecisionRecord;
      if (record.id !== next || record.siteId !== site.id || pathToGoDecisionStage(record) !== decisionStage) {
        throw new Error('Decision history belongs to another site or stage.');
      }
      rows.push(record);
      next = record.supersedesDecisionId;
    }
    return rows;
  }
  const records = collection(
    db,
    `users/${ownerUid}/sites/${site.id}/${RECORDS_COLLECTION}`,
  );
  const snapshot = await getDocs(query(
    records,
    orderBy('version', 'desc'),
    limit(Math.max(1, Math.min(100, maximum))),
  ));
  return snapshot.docs.map(row => row.data() as PathToGoDecisionRecord);
}
